import { z } from 'zod';
import { ACCEPTED_COUNTRIES, ORDER_LITERS_MAX, ORDER_LITERS_MIN, OTP_LENGTH, RECHARGE_MAX, RECHARGE_MIN, } from './constants.js';
import { PaymentMethod, Role } from './enums.js';
const emailField = z.string().trim().toLowerCase().email('Correo inválido');
const nameField = z.string().trim().min(1, 'Requerido').max(80);
const passwordField = z
    .string()
    .min(8, 'Mínimo 8 caracteres')
    .max(120)
    .refine((v) => /[A-Za-z]/.test(v) && /\d/.test(v), {
    message: 'Debe incluir letras y números',
});
export const registerStartSchema = z.object({
    email: emailField,
    firstName: nameField,
    lastName: nameField,
    password: passwordField,
    turnstileToken: z.string().min(1, 'Verificación requerida'),
});
export const registerVerifySchema = z.object({
    email: emailField,
    code: z
        .string()
        .length(OTP_LENGTH, `Código de ${OTP_LENGTH} dígitos`)
        .regex(/^\d+$/, 'Solo dígitos'),
});
export const loginStartSchema = z.object({
    email: emailField,
    password: passwordField,
    turnstileToken: z.string().min(1, 'Verificación requerida'),
});
export const otpResendSchema = z.object({
    email: emailField,
});
export const createOrderSchema = z.object({
    fuelVariantId: z.string().min(1),
    stationId: z.string().min(1),
    liters: z
        .number()
        .int()
        .min(ORDER_LITERS_MIN, `Mínimo ${ORDER_LITERS_MIN} L`)
        .max(ORDER_LITERS_MAX, `Máximo ${ORDER_LITERS_MAX} L`),
    slotId: z.string().min(1),
    method: z.enum([PaymentMethod.WALLET, PaymentMethod.CARD, PaymentMethod.TRANSFER]),
    authFileKey: z.string().optional().nullable(),
});
export const rechargeIntentSchema = z.object({
    amount: z.number().int().min(RECHARGE_MIN).max(RECHARGE_MAX),
    method: z.enum([PaymentMethod.CARD, PaymentMethod.TRANSFER]),
});
// Admin schemas
export const totpVerifySchema = z.object({
    userId: z.string().min(1),
    code: z.string().trim().min(6).max(16), // 6-digit TOTP or 9-char backup code (xxxx-xxxx)
});
export const totpConfirmSchema = z.object({
    code: z.string().trim().regex(/^\d{6}$/, 'Código de 6 dígitos'),
});
export const adminCreateStationSchema = z.object({
    name: z.string().trim().min(3).max(120),
    addr: z.string().trim().min(3).max(240),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    pumps: z.number().int().min(1).max(40),
    ev: z.boolean(),
    open: z.string().min(1),
    active: z.boolean().default(true),
});
export const adminUpdateStationSchema = adminCreateStationSchema.partial();
export const adminCreateVariantSchema = z.object({
    family: z.enum(['gasolina', 'diesel', 'ev']),
    name: z.string().trim().min(2).max(80),
    octane: z.string().trim().min(1).max(20),
    price: z.number().positive().max(100),
    tag: z.string().trim().max(40).optional().nullable(),
    desc: z.string().trim().max(240),
    comingSoon: z.boolean().default(false),
    order: z.number().int().default(0),
});
export const adminUpdateVariantSchema = adminCreateVariantSchema.partial();
export const adminUpdateUserSchema = z.object({
    role: z.enum([Role.CLIENT, Role.OPERATOR, Role.ADMIN]).optional(),
    banned: z.boolean().optional(),
    firstName: nameField.optional(),
    lastName: nameField.optional(),
});
export const adminWalletAdjustSchema = z.object({
    userId: z.string().min(1),
    amount: z.number().refine((v) => v !== 0, 'No puede ser 0'),
    reason: z.string().trim().min(3).max(240),
});
export const adminReviewAuthFileSchema = z.object({
    orderId: z.string().min(1),
    decision: z.enum(['approved', 'rejected']),
    note: z.string().trim().max(240).optional(),
});
export const userBillingSchema = z.object({
    phone: z
        .string()
        .trim()
        .regex(/^\+?\d{7,15}$/, 'Teléfono inválido (solo dígitos, opcional + al inicio, 7–15 dígitos)'),
    line1: z.string().trim().min(3, 'Mínimo 3 caracteres').max(120).optional(),
    city: z.string().trim().min(2).max(80),
    state: z.string().trim().min(2).max(80),
    country: z
        .string()
        .trim()
        .length(2)
        .transform((c) => c.toUpperCase())
        .refine((c) => ACCEPTED_COUNTRIES.some((x) => x.code === c), 'País no soportado'),
    zipCode: z.string().trim().min(3).max(15),
});
export const presignedUploadSchema = z.object({
    filename: z.string().trim().min(1).max(240),
    mime: z.enum(['application/pdf', 'image/jpeg', 'image/png']),
    size: z.number().int().positive().max(10 * 1024 * 1024),
});
//# sourceMappingURL=schemas.js.map